import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiPost } from '../../utils/api';
import { Button, Input, Spinner } from '../design-system';
import { X, Mail, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';

const InviteMemberModal = ({ isOpen, onClose, onInviteSent }) => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiPost('/.netlify/functions/invite-member', {
        email: email.trim(),
        role: 'therapist'
      }, user);

      toast.success('Invitation sent!');
      setEmail('');
      if (onInviteSent) {
        onInviteSent();
      }
      onClose();
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error(error.message || 'Unable to send invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Invite Therapist</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="therapist@example.com"
                required
                disabled={isSubmitting}
                style={{ paddingLeft: '48px' }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              They'll receive an email with instructions to join your practice
            </p>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !email.trim()}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Spinner size="small" />
                  <span className="ml-2">Sending...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteMemberModal;

